import { useMemo, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { SdrComparisonTable } from '@/components/dashboard/SdrComparisonTable'
import { CarteiraCustomerList } from '@/components/carteiras/CarteiraCustomerList'
import { useAuthStore } from '@/stores/auth'
import type { DashboardSdrComparisonResponse } from '@sdr-agend/admin-schemas'

/**
 * Story 12.10 (Epic E12) — tela dedicada pra manager/admin acompanharem a
 * carteira de cada SDR (pedido direto do usuário, depois de criar 3 SDRs
 * novas em produção).
 *
 * RBAC: admin/manager only, mesmo padrão de `PromptAssistant.tsx` (a rota
 * fica acessível mas a página redireciona quem não tem permissão — a
 * barreira real é o `rbac()` dos 2 endpoints reaproveitados abaixo).
 *
 * Reaproveita 100% de endpoints já existentes (Article IV — nenhum endpoint
 * novo):
 *   - `GET /admin/dashboard/sdr-comparison` (Story 12.9) — lista de SDRs +
 *     resumo do período. É a ÚNICA fonte de lista de SDRs usada aqui —
 *     `GET /admin/users` é admin-only, manager não tem acesso a ele.
 *   - `GET /admin/conversations?sdrId=X` (Story 12.4), sem `dateFrom`/
 *     `dateTo`, ao expandir uma linha — lista a carteira INTEIRA da SDR
 *     (não um recorte do período do resumo acima).
 *
 * Somente leitura — sem ação de reatribuição (isso já existe em
 * `Users.tsx` → `CarteiraDialog`, admin-only, Story 12.6). Não expande
 * escopo além do pedido do usuário ("ver e acompanhar").
 */
// 365 (não 366) — o backend rejeita > MAX_PERIOD_DAYS=366; ficar 1 dia
// abaixo do teto evita qualquer imprecisão de milissegundos derrubar a
// consulta com period_too_large.
const PERIOD_DAYS = 365

function lastNDaysPeriod(): { from: string; to: string } {
  const to = new Date()
  const from = new Date(to.getTime() - PERIOD_DAYS * 24 * 60 * 60 * 1000)
  return { from: from.toISOString(), to: to.toISOString() }
}

export function CarteirasPage(): React.ReactElement | null {
  const role = useAuthStore((s) => s.user?.role)
  const canView = role === 'admin' || role === 'manager'
  const [expandedSdrId, setExpandedSdrId] = useState<string | null>(null)

  // Computed ONCE per mount (not per render) — `to: new Date()` would
  // otherwise produce a new millisecond-precision value on every render,
  // making the queryKey below unstable (new "distinct" query every render,
  // re-triggering `isLoading` and unmounting the table + any expanded row).
  const period = useMemo(() => lastNDaysPeriod(), [])
  const { data, isLoading, isError } = useQuery<DashboardSdrComparisonResponse>({
    queryKey: ['dashboard-sdr-comparison', period.from, period.to],
    queryFn: async () =>
      api<DashboardSdrComparisonResponse>(
        `/admin/dashboard/sdr-comparison?from=${period.from}&to=${period.to}`,
      ),
    enabled: canView,
    staleTime: 30 * 1000,
  })

  if (!canView) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Carteiras por SDR</h1>
        <p className="text-sm text-muted-foreground">
          Acompanhe quais clientes estão na carteira de cada SDR. Clique numa linha pra ver a
          lista completa de leads dela (histórico inteiro, não só o período do resumo abaixo).
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Resumo por SDR</CardTitle>
          <CardDescription>
            Últimos {PERIOD_DAYS} dias — leads recebidos, conversas ativas, reuniões marcadas e
            taxa de resposta. Uma SDR desativada só aparece aqui se tiver lead nesse período; a
            carteira dela inteira ainda é visível expandindo a linha.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading && (
            <div className="flex h-24 items-center justify-center text-sm text-muted-foreground">
              Carregando…
            </div>
          )}
          {isError && (
            <div className="flex h-24 items-center justify-center text-sm text-destructive">
              Não foi possível carregar o resumo de carteiras.
            </div>
          )}
          {data !== undefined && !isLoading && !isError && (
            <SdrComparisonTable
              data={data}
              expandedSdrId={expandedSdrId}
              onToggleRow={(sdrId) => setExpandedSdrId((cur) => (cur === sdrId ? null : sdrId))}
              renderExpanded={(row) => <CarteiraCustomerList sdrId={row.sdrId} />}
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default CarteirasPage
